import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import useHiddenBodyOverflow from "../utils/useHiddenBodyOverflow";
import { api } from "../utils/api";
import auth from "../utils/auth";
import { track } from "../utils/track";

/**
 * '내 악보 만들기' 알림 (2026-10-17 사장님 승인 c-myscore-promo).
 * 열람 가능한 회원(0·1·4)에게 홈에서 한 번만 띄운다. 뜬 순간 이 기기와 서버에 '봤음'을 남겨 다른 기기에서도 다시 안 뜬다.
 */
const NOTICE_KEY = "my-score-2026-10";
const STORAGE_KEY = `noticeDismissed:${NOTICE_KEY}`;

const seen = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const MyScoreNotice: React.FC<{ blocked: boolean }> = ({ blocked }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  useHiddenBodyOverflow(open);

  useEffect(() => {
    if (blocked || seen() || !auth.getToken() || !["0", "1", "4"].includes(auth.getPayStatus() || "")) return;
    let canceled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/notice/dismissed");
        if (data.success && data.data.includes(NOTICE_KEY)) {
          try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
          return;
        }
      } catch (e) {
        return; // 서버 확인이 안 되면 띄우지 않는다 (여러 번 뜨는 것보다 낫다)
      }
      if (canceled) return;
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
      api.post("/notice/dismiss", { key: NOTICE_KEY }).catch(() => null);
      track("myscore-notice:show");
      setOpen(true);
    }, 2500);
    return () => {
      canceled = true;
      clearTimeout(t);
    };
  }, [blocked]);

  if (!open) return null;

  const go = () => {
    track("myscore-notice:go");
    setOpen(false);
    history.push("/my-scores/new");
  };

  return ReactDOM.createPortal(
    <Backdrop onClick={() => setOpen(false)}>
      <Dialog role="dialog" aria-modal="true" aria-labelledby="myscore-notice-title" onClick={(e) => e.stopPropagation()}>
        <Badge>새 기능</Badge>
        <Title id="myscore-notice-title">유튜브 주소만 넣으면 내 코드 악보가 생겨요</Title>
        <Lead>치고 싶은 곡의 유튜브 주소를 붙여 넣으면 AI가 몇 분 안에 코드 악보를 만들어 드려요. 만든 악보는 '내 악보'에 모여요.</Lead>
        <Go type="button" onClick={go}>만들어 보기</Go>
        <Later type="button" onClick={() => setOpen(false)}>다음에</Later>
      </Dialog>
    </Backdrop>,
    document.getElementById("root") as HTMLElement,
  );
};

export default MyScoreNotice;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(4, 4, 4, 0.7);
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 380px;
  padding: 26px 22px 16px;
  box-sizing: border-box;
  border-radius: 16px;
  background: white;
  color: #1e1e1e;
  text-align: center;
  word-break: keep-all;
  html[color-mode='dark'] & {
    background: #212632;
    color: #f2f2f2;
  }
`;

const Badge = styled.span`
  align-self: center;
  margin-bottom: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 181, 71, 0.18);
  color: #d98a00;
  font-size: 12px;
  font-weight: bold;
  html[color-mode='dark'] & {
    color: #ffb547;
  }
`;

const Title = styled.h2`
  margin: 0 0 10px;
  font-size: 19px;
  font-weight: bold;
  line-height: 1.4;
`;

const Lead = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.8;
`;

const Go = styled.button`
  height: 50px;
  border: 0;
  border-radius: 12px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
`;

const Later = styled.button`
  height: 44px;
  margin-top: 6px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 14px;
  opacity: 0.7;
  cursor: pointer;
`;
